import { useCallback, useMemo, useState } from 'react';
import { FEATURE_FONT_FAMILY_DROPDOWN_RENDER_IN_FONT } from '../../flags';
import { loadFontPreview } from './load-font-preview';
import { buildLocalFontInfo } from './load-font-info';

// 전역 font loading 상태
const globalLoadedFonts = new Set<string>();
// 로딩 실패한 폰트 (재시도 방지)
const failedFonts = new Set<string>();

export const useFontPreviewLoader = () => {
  const [loadedFonts, setLoadedFonts] = useState(
    () => new Set(globalLoadedFonts),
  );

  // 프리뷰 렌더링 시 필요한 폰트를 지연 로딩
  const loadFontForPreview = useCallback(
    async (fontFamily: string): Promise<boolean> => {
      if (!FEATURE_FONT_FAMILY_DROPDOWN_RENDER_IN_FONT) {
        return true;
      }

      // font가 이미 로드된 경우 즉시 반환
      const { family } = buildLocalFontInfo(fontFamily);
      if (globalLoadedFonts.has(family)) {
        return true;
      }

      // 이미 실패한 폰트는 재시도하지 않음
      if (failedFonts.has(family)) {
        return false;
      }

      try {
        // 프리뷰 전용 FontFace 로 로컬 woff 파일을 로드
        await loadFontPreview(family);

        // 전역 및 로컬 상태를 업데이트
        globalLoadedFonts.add(family);
        setLoadedFonts((prev) => new Set([...prev, family]));

        return true;
      } catch (error) {
        console.error(`❌ Failed to load font preview for ${family}:`, error);
        failedFonts.add(family);

        // 실패해도 loaded로 표시 (UI가 깨지지 않도록)
        globalLoadedFonts.add(family);
        setLoadedFonts((prev) => new Set([...prev, family]));

        return false;
      }
    },
    [],
  );

  const memoized = useMemo(
    () => ({
      loadFontForPreview,
      loadedFonts,
    }),
    [loadFontForPreview, loadedFonts],
  );

  return memoized;
};
